import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useAPI } from '../api';
import { Role } from 'types';

interface DataCacheContextValue {
	/** Cached roles keyed by tenantID. A key is present once the fetch for that tenant completes. */
	roles: Record<string, Role[]>;
	/**
	 * Trigger a load of roles for the given tenantID if not already cached or in-flight.
	 * Safe to call on every render — repeated calls for the same tenantID are no-ops.
	 */
	loadRoles: (tenantID: string) => void;
}

const DataCacheContext = createContext<DataCacheContextValue | null>(null);

export function useDataCache(): DataCacheContextValue {
	const ctx = useContext(DataCacheContext);
	if (!ctx) throw new Error('useDataCache must be used within DataCacheProvider');
	return ctx;
}

/**
 * Returns the cached roles for the given tenantID and triggers a fetch if not already loaded.
 * Returns an empty array while the fetch is in-flight.
 */
export function useRoles(tenantID: string | null | undefined): Role[] {
	const { roles, loadRoles } = useDataCache();

	React.useEffect(() => {
		if (tenantID) loadRoles(tenantID);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tenantID]);

	return tenantID ? (roles[tenantID] ?? []) : [];
}

const DataCacheProvider = ({ children }: { children: React.ReactNode }) => {
	const api = useAPI();
	// Keep a ref to the latest API instance so the stable loadRoles callback always uses it.
	const apiRef = useRef(api);
	apiRef.current = api;

	const [roles, setRoles] = useState<Record<string, Role[]>>({});
	// rolesRef mirrors roles state so loadRoles can check it without being in its dep array.
	const rolesRef = useRef<Record<string, Role[]>>({});
	// Tracks in-flight fetches to prevent duplicate requests.
	const fetchingRef = useRef<Record<string, boolean>>({});

	const loadRoles = useCallback((tenantID: string) => {
		if (rolesRef.current[tenantID] || fetchingRef.current[tenantID]) return;
		fetchingRef.current[tenantID] = true;
		const endpoint = tenantID === 'system' ? '/api/roles' : `/api/t/${tenantID}/roles`;
		apiRef.current.get<Role[]>(endpoint)
			.then(json => {
				rolesRef.current[tenantID] = json;
				setRoles(prev => ({ ...prev, [tenantID]: json }));
			})
			.catch(() => {
				// Allow a retry on error by clearing the in-flight flag.
				delete fetchingRef.current[tenantID];
			});
	}, []); // stable — intentionally no deps

	const value: DataCacheContextValue = { roles, loadRoles };

	return (
		<DataCacheContext.Provider value={value}>
			{children}
		</DataCacheContext.Provider>
	);
};

export default DataCacheProvider;
